/**
 * AiRankThresholds — per-organisation AI Fit bands.
 *
 * Every firm draws these lines differently: one chasing large primes wants a
 * higher bar for Blue than one hunting subcontract work. So the bands belong to
 * the client org rather than the platform.
 *
 * Saving re-bands existing opportunities immediately. That is cheap because
 * each opportunity's raw score is already stored — re-banding is arithmetic
 * over stored scores, with no re-analysis and no AI spend. The panel says so,
 * because "this will re-score everything" is the natural fear otherwise.
 */
import { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, RotateCcw, Save, Info } from 'lucide-react';
import { AI_RANK_CONFIG } from '../../lib/aiRank';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BANDS = ['BLUE', 'GREEN', 'YELLOW', 'RED'];

export default function AiRankThresholds({ organizationId }) {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaults, setDefaults] = useState(null);
  const [form, setForm] = useState({});
  const [dist, setDist] = useState({});
  const [scored, setScored] = useState(0);
  const [customized, setCustomized] = useState(false);

  const qs = organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : '';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/sentinel/ai-rank-thresholds${qs}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setForm(d.thresholds || {});
      setDefaults(d.defaults || null);
      setDist(d.distribution_at_current_thresholds || {});
      setScored(d.scored_opportunities || 0);
      setCustomized(!!d.is_customized);
    } catch {
      toast.error('Could not load AI Fit thresholds');
    } finally {
      setLoading(false);
    }
  }, [qs, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  // Mirrors the server's rule so the warning appears before saving, not after.
  const descendingBroken = BANDS.slice(1).some((b, i) => {
    const hi = Number(form[BANDS[i]]);
    const lo = Number(form[b]);
    return Number.isFinite(hi) && Number.isFinite(lo) && lo >= hi;
  });

  const save = async () => {
    setSaving(true);
    try {
      const body = { recolor: true };
      if (organizationId) body.organization_id = organizationId;
      BANDS.forEach((b) => { if (form[b] !== '' && form[b] != null) body[b] = Number(form[b]); });

      const res = await fetch(`${API_URL}/api/sentinel/ai-rank-thresholds`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Save failed');
      toast.success(d.message, { duration: 8000 });
      if (d.adjusted_to_keep_descending) {
        toast.warning(
          `Adjusted to keep bands descending: ${JSON.stringify(d.adjusted_to_keep_descending)}`,
          { duration: 9000 },
        );
      }
      await load();
    } catch (e) {
      toast.error(e.message, { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => defaults && setForm({ ...defaults });

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading AI Fit thresholds…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">AI Fit thresholds</h3>
        <p className="text-xs text-slate-500 mt-0.5">
          The minimum score for each band. A score is the share of an opportunity's requirements
          your firm can address, judged against your Knowledge Base.
          {!customized && ' Currently using platform defaults.'}
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {BANDS.map((band) => (
          <div key={band} className="space-y-1">
            <Label className="text-xs text-slate-600 flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${AI_RANK_CONFIG[band]?.dot}`} />
              {AI_RANK_CONFIG[band]?.label} ≥
            </Label>
            <Input
              type="number" min={1} max={100}
              value={form[band] ?? ''}
              onChange={(e) => setForm({ ...form, [band]: e.target.value })}
              className="h-8 text-sm font-mono"
              data-testid={`ai-rank-threshold-${band.toLowerCase()}`}
            />
          </div>
        ))}
      </div>

      <p className="text-xs text-slate-500">
        Anything below {AI_RANK_CONFIG.RED?.label}'s cut-off is{' '}
        <span className="font-medium text-slate-700">{AI_RANK_CONFIG.GRAY?.label}</span>.
      </p>

      {descendingBroken && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          Bands must descend — Blue above Green above Yellow above Red. Saving will adjust the
          lower band rather than reject your change.
        </p>
      )}

      {scored > 0 && (
        <div className="rounded border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs text-slate-600 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 mt-0.5 flex-none text-slate-400" />
            <span>
              {scored} ranked opportunit{scored === 1 ? 'y' : 'ies'} would sit as shown below.
              Saving re-bands them from their stored scores — <strong>no re-analysis, no AI cost</strong>.
            </span>
          </p>
          <div className="flex flex-wrap gap-3 mt-2">
            {['BLUE', 'GREEN', 'YELLOW', 'RED', 'GRAY'].map((b) => (
              <span key={b} className="flex items-center gap-1.5 text-xs text-slate-700">
                <span className={`w-2 h-2 rounded-full ${AI_RANK_CONFIG[b]?.dot}`} />
                {AI_RANK_CONFIG[b]?.label}
                <span className="font-mono font-semibold">{dist[b] ?? 0}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button size="sm" onClick={save} disabled={saving} data-testid="ai-rank-thresholds-save">
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
          Save &amp; re-band
        </Button>
        <Button size="sm" variant="outline" onClick={resetToDefaults} disabled={saving || !defaults}>
          <RotateCcw className="w-3.5 h-3.5 mr-1" />
          Platform defaults
        </Button>
      </div>
    </div>
  );
}
